// KLUB-67 (#256) — U1. Availability window: canonical shape, weekday vocabulary,
// range normalization, the display-only mirror evaluator and the pt-BR sentence
// generator.
//
// THE MIRROR IS NEVER THE GATE. `evaluateAvailability` exists so the app can
// paint a badge, filter a list and describe the rule, and so the admin can read
// back what it just configured. Authorization lives in the DB function
// `benefit_availability_state` (U3), which the two coupon-minting edge functions
// call with the *server* clock. The two implementations can drift; the damage is
// capped at a wrong badge because the mirror never authorizes anything.
//
// Two deliberate divergences from `apps/mobile/lib/openNow.ts`, the closest
// precedent in the repo:
//   1. An unresolvable timezone REFUSES here (state `unresolved_timezone`).
//      `openNow.ts` falls back to UTC, which fails open — the opposite of what a
//      gate on courtesy coupons needs.
//   2. Absence of a window means AVAILABLE here. In `openNow.ts` a benefit with
//      no `operating_hours` is excluded from "Aberto agora" (it advertises no
//      schedule); here, no window means no time restriction at all (R1).
//
// Start is inclusive, end is exclusive — inherited from `openNow.ts` so the two
// evaluators agree on boundary instants.

// ============================================
// Weekday vocabulary
// ============================================

export type WeekdayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

/** Display order (Monday first), and the order day groups are folded in. */
export const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

/** Index-aligned with `Date#getUTCDay()` / `Intl` numeric weekday (0 = Sunday). */
const WEEKDAY_KEYS_SUNDAY_FIRST: readonly WeekdayKey[] = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
];

/** Full pt-BR day names — used for the per-day lines of the full rule. */
export const WEEKDAY_LABELS: Record<WeekdayKey, string> = {
  mon: "Segunda-feira",
  tue: "Terça-feira",
  wed: "Quarta-feira",
  thu: "Quinta-feira",
  fri: "Sexta-feira",
  sat: "Sábado",
  sun: "Domingo",
};

/** Short pt-BR day names — used for grouped summaries ("de terça a sexta"). */
export const WEEKDAY_SHORT_LABELS: Record<WeekdayKey, string> = {
  mon: "Segunda",
  tue: "Terça",
  wed: "Quarta",
  thu: "Quinta",
  fri: "Sexta",
  sat: "Sábado",
  sun: "Domingo",
};

// ============================================
// Shape
// ============================================

/**
 * A single time range inside a day. `"HH:MM"`, 24h clock, tenant-local.
 *
 * `end` may be lower than `start`, which means the range crosses midnight and
 * runs until `end` of the *next* day (R9 / AE6). `end` equal to `start` is
 * rejected by the DB CHECK — a zero-length range reads as closed, so "all day"
 * is spelled `00:00`–`24:00`, never `00:00`–`00:00`.
 */
export interface AvailabilityRange {
  start: string;
  end: string;
}

/**
 * The window itself, persisted as `benefits.availability_window` (JSONB).
 *
 * A day key absent from `days`, or present with an empty array, is CLOSED. The
 * gate has a single rule — "does some range cover this instant?" — so absence
 * always fails closed. That asymmetry is load-bearing: an `else` branch that
 * emptied the list would open the benefit 24h instead of closing it, which is
 * the KLUB-66 failure mode pointed at a gate that spends the club's money.
 *
 * `exceptions` is keyed by `"YYYY-MM-DD"` (tenant-local date) and REPLACES the
 * weekday rule for that date entirely — an empty array closes a normally-open
 * date, a populated one opens a normally-closed date (R8 / AE3 / AE4).
 */
export interface AvailabilityWindow {
  days: Partial<Record<WeekdayKey, AvailabilityRange[]>>;
  exceptions?: Record<string, AvailabilityRange[]>;
}

/**
 * The window plus its validity envelope. Validity lives in its own DATE columns
 * (`availability_starts_on` / `availability_ends_on`) and outranks both the
 * weekday rule and any exception (R8 / AE5).
 *
 * `window: null` means no time restriction at all — the benefit is available
 * whenever the validity envelope allows (R1 / AE8).
 */
export interface AvailabilityRule {
  window: AvailabilityWindow | null;
  /** `"YYYY-MM-DD"` or null. */
  startsOn: string | null;
  /** `"YYYY-MM-DD"` or null. */
  endsOn: string | null;
}

/** The three `benefits` columns this module reads, as a structural type. */
export interface BenefitAvailabilityFields {
  availability_window: AvailabilityWindow | null;
  availability_starts_on: string | null;
  availability_ends_on: string | null;
}

/** Lifts the three benefit columns into an {@link AvailabilityRule}. */
export function availabilityRuleOf(benefit: BenefitAvailabilityFields): AvailabilityRule {
  return {
    window: benefit.availability_window ?? null,
    startsOn: benefit.availability_starts_on ?? null,
    endsOn: benefit.availability_ends_on ?? null,
  };
}

/**
 * `available` — mint away.
 * `closed` — the window is shut right now; {@link AvailabilityEvaluation.nextOpeningAt}
 *   carries the return, when there is one.
 * `unresolved_timezone` — the tenant timezone did not resolve. A refusal, never
 *   narrated to the member as a schedule (R6 / R12 / AE10).
 */
export type AvailabilityState = "available" | "closed" | "unresolved_timezone";

export interface AvailabilityEvaluation {
  state: AvailabilityState;
  /**
   * Next instant the window opens, or null when there is none: validity already
   * ended, no day carries a range, or the return falls beyond
   * {@link NEXT_OPENING_HORIZON_DAYS}. Always null when `state !== "closed"`.
   */
  nextOpeningAt: Date | null;
}

/** A day marked without editing hours (R3). */
export const ALL_DAY_RANGE: AvailabilityRange = { start: "00:00", end: "24:00" };

/**
 * How far ahead {@link evaluateAvailability} looks for the next opening. Seven
 * days covers the week turn; past that the badge says "closed" without promising
 * a return, which beats an unbounded scan over a window that never opens.
 */
export const NEXT_OPENING_HORIZON_DAYS = 7;

const MINUTES_PER_DAY = 24 * 60;

// ============================================
// Time-of-day primitives
// ============================================

/** Parses `"HH:MM"` into minutes since midnight (`"24:00"` → 1440). Null when malformed. */
export function parseTimeOfDay(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (minutes > 59) return null;
  const total = hours * 60 + minutes;
  if (total > MINUTES_PER_DAY) return null;
  return total;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Formats minutes since midnight back to `"HH:MM"` (1440 → `"24:00"`). */
export function formatTimeOfDay(minutes: number): string {
  return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
}

/** `"11:00"` → `"11h"`, `"11:30"` → `"11h30"`. Falls back to the raw value. */
export function formatTimeLabel(value: string): string {
  const total = parseTimeOfDay(value);
  if (total === null) return value;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h${pad2(minutes)}`;
}

// ============================================
// Normalization (read-side only)
// ============================================

/** A range expanded to absolute minutes; `end` exceeds 1440 when it wraps. */
interface SpanMinutes {
  start: number;
  end: number;
}

function toSpans(ranges: readonly AvailabilityRange[]): SpanMinutes[] {
  const spans: SpanMinutes[] = [];
  for (const range of ranges) {
    const start = parseTimeOfDay(range.start);
    const rawEnd = parseTimeOfDay(range.end);
    if (start === null || rawEnd === null) continue;
    // Zero-length is rejected by the DB CHECK; if one ever slips in, drop it
    // rather than reading it as a full day.
    if (start === rawEnd) continue;
    if (start >= MINUTES_PER_DAY) continue;
    spans.push({ start, end: rawEnd < start ? rawEnd + MINUTES_PER_DAY : rawEnd });
  }
  return spans.sort((a, b) => a.start - b.start || a.end - b.end);
}

function mergeSpans(spans: SpanMinutes[]): SpanMinutes[] {
  const merged: SpanMinutes[] = [];
  for (const span of spans) {
    const last = merged[merged.length - 1];
    if (last && span.start <= last.end) {
      last.end = Math.max(last.end, span.end);
    } else {
      merged.push({ ...span });
    }
  }
  // A span that wraps past midnight can reconnect with the first span of the
  // same day (22h–02h plus 01h–03h reads as 22h–03h, not two ranges).
  while (merged.length > 1) {
    const last = merged[merged.length - 1];
    const first = merged[0];
    if (last.end - MINUTES_PER_DAY < first.start) break;
    last.end = Math.max(last.end, first.end + MINUTES_PER_DAY);
    merged.shift();
  }
  return merged;
}

/**
 * Sorts and folds overlapping or touching ranges. Read-side only: the stored
 * JSONB stays exactly what the staff typed, because overlaps are inert for the
 * gate ("does some range cover now?") and only break the sentence — 11h–15h plus
 * 14h–18h has to read "das 11h às 18h".
 */
export function normalizeRanges(ranges: readonly AvailabilityRange[]): AvailabilityRange[] {
  const merged = mergeSpans(toSpans(ranges));
  if (merged.length === 0) return [];
  if (merged.some((span) => span.end - span.start >= MINUTES_PER_DAY)) {
    return [{ ...ALL_DAY_RANGE }];
  }
  return merged.map((span) => ({
    start: formatTimeOfDay(span.start % MINUTES_PER_DAY),
    end: formatTimeOfDay(span.end > MINUTES_PER_DAY ? span.end - MINUTES_PER_DAY : span.end),
  }));
}

/**
 * True when the ranges (already normalized) cover the whole day. Both the admin
 * editor and the member detail need this to render "dia inteiro" instead of
 * "das 0h às 24h", so it lives here rather than as two literal comparisons.
 */
export function isAllDayRanges(ranges: readonly AvailabilityRange[]): boolean {
  return (
    ranges.length === 1 &&
    ranges[0].start === ALL_DAY_RANGE.start &&
    ranges[0].end === ALL_DAY_RANGE.end
  );
}

const isAllDay = isAllDayRanges;

// ============================================
// Tenant-local calendar helpers
// ============================================

interface ZonedInstant {
  /** `"YYYY-MM-DD"` in the tenant timezone. */
  date: string;
  /** Minutes since local midnight. */
  minutes: number;
}

function zonedInstant(at: Date, timeZone: string): ZonedInstant | null {
  if (!timeZone) return null;
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(at);
  } catch {
    // No UTC fallback on purpose — see the header note. An unresolvable
    // timezone is a configuration failure, and this evaluator refuses.
    return null;
  }

  const read = (type: Intl.DateTimeFormatPartTypes): string | undefined =>
    parts.find((part) => part.type === type)?.value;

  const year = read("year");
  const month = read("month");
  const day = read("day");
  if (!year || !month || !day) return null;

  // `hour: "2-digit"` with `hour12: false` yields "24" at midnight on some
  // engines (Hermes, older Node) — same normalization `openNow.ts` carries.
  let hour = Number(read("hour") ?? "0");
  const minute = Number(read("minute") ?? "0");
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  if (hour === 24) hour = 0;

  return { date: `${year}-${month}-${day}`, minutes: hour * 60 + minute };
}

function utcOffsetMs(at: Date, timeZone: string): number | null {
  const zoned = zonedInstant(at, timeZone);
  if (!zoned) return null;
  const [year, month, day] = zoned.date.split("-").map(Number);
  const wallClock = Date.UTC(
    year,
    month - 1,
    day,
    Math.floor(zoned.minutes / 60),
    zoned.minutes % 60,
  );
  // Offsets are whole minutes, so compare at minute precision.
  return wallClock - Math.floor(at.getTime() / 60000) * 60000;
}

/** Absolute instant of a tenant-local wall clock. Null when the timezone fails. */
function zonedTimeToInstant(date: string, minutes: number, timeZone: string): Date | null {
  const [year, month, day] = date.split("-").map(Number);
  const wallClock = Date.UTC(year, month - 1, day, Math.floor(minutes / 60), minutes % 60);
  let timestamp = wallClock;
  // Two passes: the first offset is read at the wrong instant whenever a DST
  // boundary sits between the guess and the answer.
  for (let pass = 0; pass < 2; pass += 1) {
    const offset = utcOffsetMs(new Date(timestamp), timeZone);
    if (offset === null) return null;
    timestamp = wallClock - offset;
  }
  return new Date(timestamp);
}

function addDays(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day) + days * 86400000);
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
}

/** Weekday key of a `"YYYY-MM-DD"` calendar date. */
export function weekdayOf(date: string): WeekdayKey {
  const [year, month, day] = date.split("-").map(Number);
  return WEEKDAY_KEYS_SUNDAY_FIRST[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
}

/**
 * Ranges in force on a tenant-local date: the exception when the date names one
 * (even an empty one, which closes the date), the weekday rule otherwise.
 * A null window has no restriction, so every date is a full day.
 */
function rangesOn(window: AvailabilityWindow | null, date: string): AvailabilityRange[] {
  if (!window) return [{ ...ALL_DAY_RANGE }];
  const exceptions = window.exceptions;
  if (exceptions && Object.prototype.hasOwnProperty.call(exceptions, date)) {
    return normalizeRanges(exceptions[date] ?? []);
  }
  return normalizeRanges(window.days?.[weekdayOf(date)] ?? []);
}

function withinValidity(rule: AvailabilityRule, date: string): boolean {
  if (rule.startsOn && date < rule.startsOn) return false;
  if (rule.endsOn && date > rule.endsOn) return false;
  return true;
}

// ============================================
// Evaluation
// ============================================

/**
 * Display-only mirror of the DB function. Never authorizes anything.
 *
 * Resolution order is validity → exception → weekday (R8), all read in the
 * tenant timezone, with the instant supplied by the caller so any day and hour
 * is reachable in a test (R5 / R9).
 */
export function evaluateAvailability(
  rule: AvailabilityRule,
  at: Date,
  timeZone: string,
): AvailabilityEvaluation {
  const now = zonedInstant(at, timeZone);
  if (!now) return { state: "unresolved_timezone", nextOpeningAt: null };

  const insideValidity = withinValidity(rule, now.date);

  if (insideValidity && !rule.window) return { state: "available", nextOpeningAt: null };

  if (insideValidity && covers(rule.window, now)) {
    return { state: "available", nextOpeningAt: null };
  }

  return { state: "closed", nextOpeningAt: findNextOpening(rule, now, at, timeZone) };
}

function covers(window: AvailabilityWindow | null, now: ZonedInstant): boolean {
  for (const span of toSpans(rangesOn(window, now.date))) {
    if (now.minutes >= span.start && now.minutes < Math.min(span.end, MINUTES_PER_DAY)) return true;
  }
  // Yesterday's range may still be running across midnight (AE6).
  for (const span of toSpans(rangesOn(window, addDays(now.date, -1)))) {
    if (span.end > MINUTES_PER_DAY && now.minutes < span.end - MINUTES_PER_DAY) return true;
  }
  return false;
}

function findNextOpening(
  rule: AvailabilityRule,
  now: ZonedInstant,
  at: Date,
  timeZone: string,
): Date | null {
  for (let offset = 0; offset <= NEXT_OPENING_HORIZON_DAYS; offset += 1) {
    const date = addDays(now.date, offset);
    // Past the end of validity nothing reopens, ever — say so instead of
    // scanning out the horizon and implying a return.
    if (rule.endsOn && date > rule.endsOn) return null;
    if (rule.startsOn && date < rule.startsOn) continue;
    for (const range of rangesOn(rule.window, date)) {
      const startMinutes = parseTimeOfDay(range.start);
      if (startMinutes === null) continue;
      const instant = zonedTimeToInstant(date, startMinutes, timeZone);
      if (instant && instant.getTime() > at.getTime()) return instant;
    }
  }
  return null;
}

// ============================================
// pt-BR rendering
// ============================================

/**
 * One weekday of the full rule, split so a two-column layout (day on the left,
 * hours on the right) does not have to take the joined sentence apart again.
 * Joined form is `${label}: ${text}`.
 */
export interface AvailabilityDayLine {
  day: WeekdayKey;
  /** "Terça-feira". */
  label: string;
  /** "das 11h às 15h", "dia inteiro" or "indisponível". */
  text: string;
}

export interface AvailabilityDescription {
  /** One line, grouped and folded: "De terça a sexta, das 11h às 15h". */
  summary: string;
  /** All seven days, closed ones included. */
  dayLines: AvailabilityDayLine[];
  /** One per date exception: "12/09/2026: das 18h às 23h". */
  exceptionLines: string[];
  /** "Válido de 01/08/2026 a 31/08/2026", or null when unbounded. */
  validityLine: string | null;
}

function formatDateLabel(date: string): string {
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
}

/** "das 11h às 15h", or "das 11h às 15h e das 18h às 23h". */
function formatRanges(ranges: readonly AvailabilityRange[]): string {
  return ranges
    .map((range) => `das ${formatTimeLabel(range.start)} às ${formatTimeLabel(range.end)}`)
    .join(" e ");
}

function formatDayGroup(days: readonly WeekdayKey[]): string {
  const labels = days.map((day) => WEEKDAY_SHORT_LABELS[day].toLowerCase());
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} e ${labels[1]}`;
  return `de ${labels[0]} a ${labels[labels.length - 1]}`;
}

function capitalize(text: string): string {
  return text.length === 0 ? text : text[0].toUpperCase() + text.slice(1);
}

function formatValidity(rule: AvailabilityRule): string | null {
  if (rule.startsOn && rule.endsOn) {
    return `Válido de ${formatDateLabel(rule.startsOn)} a ${formatDateLabel(rule.endsOn)}`;
  }
  if (rule.startsOn) return `Válido a partir de ${formatDateLabel(rule.startsOn)}`;
  if (rule.endsOn) return `Válido até ${formatDateLabel(rule.endsOn)}`;
  return null;
}

/**
 * Renders the rule the staff configured back in Portuguese (R16), and feeds the
 * member-facing detail (R20) and the coupon refusal text (R12) from the same
 * generator — the server ships a code plus the structured rule, never prose.
 */
export function describeAvailability(rule: AvailabilityRule): AvailabilityDescription {
  const validityLine = formatValidity(rule);

  if (!rule.window) {
    return {
      summary: "Disponível a qualquer dia e horário",
      dayLines: [],
      exceptionLines: [],
      validityLine,
    };
  }

  const normalizedByDay = new Map<WeekdayKey, AvailabilityRange[]>();
  for (const day of WEEKDAY_KEYS) {
    normalizedByDay.set(day, normalizeRanges(rule.window.days?.[day] ?? []));
  }

  const dayLines: AvailabilityDayLine[] = WEEKDAY_KEYS.map((day) => {
    const ranges = normalizedByDay.get(day) ?? [];
    const text =
      ranges.length === 0
        ? "indisponível"
        : isAllDay(ranges)
          ? "dia inteiro"
          : formatRanges(ranges);
    return { day, label: WEEKDAY_LABELS[day], text };
  });

  // Fold consecutive days that carry the exact same (normalized) ranges.
  const groups: { days: WeekdayKey[]; ranges: AvailabilityRange[] }[] = [];
  for (const day of WEEKDAY_KEYS) {
    const ranges = normalizedByDay.get(day) ?? [];
    if (ranges.length === 0) continue;
    const signature = JSON.stringify(ranges);
    const previous = groups[groups.length - 1];
    const isConsecutive =
      previous &&
      WEEKDAY_KEYS.indexOf(previous.days[previous.days.length - 1]) === WEEKDAY_KEYS.indexOf(day) - 1;
    if (previous && isConsecutive && JSON.stringify(previous.ranges) === signature) {
      previous.days.push(day);
    } else {
      groups.push({ days: [day], ranges });
    }
  }

  const summary =
    groups.length === 0
      ? "Indisponível — nenhum dia aberto"
      : capitalize(
          groups
            .map((group) =>
              isAllDay(group.ranges)
                ? formatDayGroup(group.days)
                : `${formatDayGroup(group.days)}, ${formatRanges(group.ranges)}`,
            )
            .join("; "),
        );

  const exceptions = rule.window.exceptions ?? {};
  const exceptionLines = Object.keys(exceptions)
    .sort()
    .map((date) => {
      const ranges = normalizeRanges(exceptions[date] ?? []);
      if (ranges.length === 0) return `${formatDateLabel(date)}: indisponível`;
      if (isAllDay(ranges)) return `${formatDateLabel(date)}: dia inteiro`;
      return `${formatDateLabel(date)}: ${formatRanges(ranges)}`;
    });

  return { summary, dayLines, exceptionLines, validityLine };
}

/** "volta terça, 11h" — the return half of the badge (R20). Null when the timezone fails. */
export function formatNextOpening(nextOpeningAt: Date, at: Date, timeZone: string): string | null {
  const opening = zonedInstant(nextOpeningAt, timeZone);
  const now = zonedInstant(at, timeZone);
  if (!opening || !now) return null;

  const time = formatTimeLabel(formatTimeOfDay(opening.minutes));
  if (opening.date === now.date) return `hoje, ${time}`;
  if (opening.date === addDays(now.date, 1)) return `amanhã, ${time}`;
  return `${WEEKDAY_SHORT_LABELS[weekdayOf(opening.date)].toLowerCase()}, ${time}`;
}

/**
 * Badge text for a benefit that cannot be used right now, or null when it can.
 * A closed window with no return says so without promising one (R20).
 */
export function availabilityBadgeLabel(
  evaluation: AvailabilityEvaluation,
  at: Date,
  timeZone: string,
): string | null {
  if (evaluation.state === "available") return null;
  if (evaluation.state === "unresolved_timezone") return "Indisponível";
  if (!evaluation.nextOpeningAt) return "Indisponível agora";
  const back = formatNextOpening(evaluation.nextOpeningAt, at, timeZone);
  return back ? `Indisponível agora · volta ${back}` : "Indisponível agora";
}
